'use client';

import { clientCookies } from '@hyphengroup/cms-components/utils/cookies';
import {
  LeadCapturePage_offer,
  ProductWithOfferApply,
} from '__generated__/graphql';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import { getAllSearchParams } from 'utils/uri';
import { CircleProgressBar } from './CircleProgressBar';
import { RedirectPageStaticText } from './i18n';
import {
  deepLinkParamReplacement,
  macrosUri,
} from '$/(application)/rewardSourceUrlUtils';
import { getTrackingParams } from '$/(application)/tracking';

export function RedirectScreen(props: {
  product: ProductWithOfferApply['getProduct'];
  redirectUrl: URL;
  staticText: Translated<typeof RedirectPageStaticText>;
  offer: LeadCapturePage_offer;
}): JSX.Element {
  const staticText = props.staticText;
  const url = new URL(window.location.href);
  const searchParams = getAllSearchParams(url.searchParams);
  // CORE-4767: the Scaleo pair must come from the same session-scoped source as
  // the server half of this macro substitution (`redirect/page.tsx`) and as the
  // lead this screen was rendered after (`StickyQuickApply`) — otherwise the
  // provider URL and the lead disagree on the attribution.
  const tracking = getTrackingParams(clientCookies(), url.search);
  const leadParamsFromUrl = {
    UtmSource: searchParams.utm_source?.toString() ?? '',
    UtmMedium: searchParams.utm_medium?.toString() ?? '',
    UtmCampaign: searchParams.utm_campaign?.toString() ?? '',
    scaleo_click_id: tracking.clkid ?? '',
    scaleo_click_type: tracking.clktype ?? '',
  };

  const redirectUri = props.redirectUrl.toString();
  const redirectUriWithMacros = macrosUri(
    redirectUri,
    {
      utm_source: leadParamsFromUrl.UtmSource,
      utm_medium: leadParamsFromUrl.UtmMedium,
      utm_campaign: leadParamsFromUrl.UtmCampaign,
      clkid: leadParamsFromUrl.scaleo_click_id,
    },
    url.searchParams,
  );

  // DeepLink parameters to replace. Pass raw values; `deepLinkParamReplacement`
  // applies the per-key encoding policy so server and client stay in sync.
  const resultRedirectUri = deepLinkParamReplacement(
    redirectUriWithMacros ?? '/',
    {
      productId: url.searchParams.get('productId') ?? '',
      productTitle: url.searchParams.get('productTitle') ?? '',
      payloadCriteria: url.searchParams.get('criteria') ?? '',
      forwardParameters: url.searchParams.get('forwardParameters') ?? '',
    },
  );

  return (
    <div className="fixed inset-0 z-50 flex h-screen w-screen items-center justify-center overflow-hidden bg-white">
      <div className="mx-auto flex size-full max-w-screen-md flex-col items-center justify-center text-center">
        <CircleProgressBar
          imageUrl={props.product?.productImage ?? ''}
          redirectUrl={resultRedirectUri}
        />
        <div className="my-3 px-4">
          <h3 key="heading" className="alpha-display-1">
            {t(staticText.heading, {
              providerName: (
                <strong>{props.product?.provider?.providerName}</strong>
              ),
            })}
          </h3>
          <p className="mt-2 text-text/secondary text-sm md:text-base lg:text-lg">
            {props.offer ? staticText.subheading : staticText.subheadingNoOffer}
          </p>
        </div>
      </div>
    </div>
  );
}
